<template>
    <v-dialog
        :model-value="modelValue"
        max-width="1200"
        width="95vw"
        :fullscreen="$vuetify.display.smAndDown"
        persistent
        scrollable
        :z-index="3200"
        @update:model-value="$emit('update:modelValue', $event)"
    >
        <v-card class="propagate-dialog-card d-flex flex-column">
            <v-toolbar density="comfortable" class="flex-shrink-0">
                <v-toolbar-title>
                    Aplică și la alte produse ale furnizorului
                </v-toolbar-title>
                <v-spacer />
                <v-btn
                    icon
                    variant="text"
                    :disabled="saving"
                    @click="onCancel"
                >
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>

            <v-card-text class="propagate-dialog-body flex-grow-1 overflow-y-auto pt-4">
                <div class="mb-4">
                    <div class="text-caption text-medium-emphasis">Furnizor</div>
                    <div class="text-h6">{{ furnizorLabel || '—' }}</div>
                    <div class="text-body-2 text-medium-emphasis mt-1">
                        Produs sursă:
                        <strong>{{ sourceProductCode || '—' }}</strong>
                        <span v-if="sourceProductName">
                            — {{ sourceProductName }}
                        </span>
                    </div>
                </div>

                <div class="text-subtitle-2 mb-2">Ce copiez</div>
                <p
                    v-if="directListSave"
                    class="text-body-2 text-medium-emphasis mb-2"
                >
                    În listă se copiază doar Detalii produs, note furnizor, termen de plată și adaos referință.
                    Câmpurile de preț rămân vizibile, dar sunt dezactivate — se aplică din lista în lucru.
                </p>
                <div class="d-flex flex-wrap ga-2 mb-2">
                    <v-btn
                        size="small"
                        variant="text"
                        :disabled="saving || loading"
                        @click="selectAllFields"
                    >
                        Toate câmpurile
                    </v-btn>
                    <v-btn
                        size="small"
                        variant="text"
                        :disabled="saving || loading"
                        @click="clearFields"
                    >
                        Niciun câmp
                    </v-btn>
                </div>
                <v-row dense class="mb-4">
                    <v-col
                        v-for="field in fieldDefinitions"
                        :key="field.key"
                        cols="12"
                        sm="6"
                        md="4"
                    >
                        <v-tooltip location="top" open-on-hover :open-delay="200">
                            <template #activator="{ props: tooltipProps }">
                                <div v-bind="tooltipProps">
                                    <v-checkbox
                                        v-model="selectedFieldKeys"
                                        :label="field.label"
                                        :value="field.key"
                                        density="compact"
                                        hide-details
                                        :disabled="saving || loading || isFieldDisabledForDirectSave(field.key)"
                                    />
                                </div>
                            </template>
                            <span v-if="isFieldDisabledForDirectSave(field.key)">
                                Disponibil doar la salvarea în lista în lucru.
                            </span>
                            <span v-else>
                                Valoare curentă:
                                <strong>{{ fieldCurrentValue(field.key) }}</strong>
                            </span>
                        </v-tooltip>
                    </v-col>
                </v-row>

                <div class="text-subtitle-2 mb-2">Filtre</div>
                <v-row dense class="mb-3">
                    <v-col cols="12" md="4">
                        <v-text-field
                            v-model="criteria.search"
                            label="Căutare (cod / denumire produs și furnizor)"
                            density="compact"
                            variant="outlined"
                            clearable
                            hide-details="auto"
                            prepend-inner-icon="mdi-magnify"
                            :disabled="saving"
                            @update:model-value="onCriteriaInput"
                        />
                    </v-col>
                    <v-col cols="12" md="4">
                        <HierarchicalFilterSelect
                            v-model:parent="criteria.systems"
                            v-model:child="criteria.systemSubcategory"
                            label="Sistem / Subsistem"
                            parent-label="Sistem"
                            child-label="Subsistem"
                            :parent-items="categoryOptions"
                            :child-items="subcategoryOptions"
                            :child-loading="loadingSubcategories"
                            multiple
                            @parent-change="onSystemsFilterChange"
                            @update:child="onCriteriaInput"
                        />
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-autocomplete
                            v-model="criteria.manufacturerCode"
                            :items="manufacturerOptions"
                            item-title="title"
                            item-value="value"
                            label="Producător"
                            density="compact"
                            variant="outlined"
                            clearable
                            hide-details="auto"
                            :custom-filter="diacriticInsensitiveFilter"
                            auto-select-first
                            :disabled="saving"
                            @update:model-value="onCriteriaInput"
                        />
                    </v-col>
                    <v-col cols="12" md="4">
                        <HierarchicalFilterSelect
                            v-model:parent="criteria.chapterCode"
                            v-model:child="criteria.subchapterCode"
                            label="Categorie / Subcategorie"
                            parent-label="Categorie"
                            child-label="Subcategorie"
                            :parent-items="chapterOptions"
                            :child-items="subchapterOptions"
                            multiple
                            @parent-change="onChapterFilterChange"
                            @update:child="onCriteriaInput"
                        />
                    </v-col>
                </v-row>

                <div class="d-flex flex-wrap align-center justify-space-between ga-2 mb-2">
                    <div class="text-subtitle-2 mb-0">
                        Pe ce produse
                        <span class="text-medium-emphasis font-weight-regular">
                            · {{ selectedProductCodes.length }} selectate
                            <template v-if="peersTotalCount">
                                · {{ peersTotalCount }} pe listă
                            </template>
                        </span>
                    </div>
                    <div class="d-flex flex-wrap ga-2">
                        <v-btn
                            size="small"
                            variant="text"
                            :disabled="!peersTotalCount || saving || loading || selectingAll"
                            :loading="selectingAll"
                            @click="selectAllProducts"
                        >
                            Selectează toate
                        </v-btn>
                        <v-btn
                            size="small"
                            variant="text"
                            :disabled="!selectedProductCodes.length || saving || loading || selectingAll"
                            @click="clearProducts"
                        >
                            Niciuna
                        </v-btn>
                    </div>
                </div>

                <v-alert
                    v-if="markupReferenceLockedCodes.length"
                    type="warning"
                    variant="tonal"
                    density="compact"
                    class="mb-3"
                >
                    {{ markupReferenceLockedMessage }}
                </v-alert>

                <v-alert
                    v-if="loading && !tableRows.length"
                    type="info"
                    variant="tonal"
                    density="compact"
                    class="mb-3"
                >
                    Se încarcă produsele furnizorului…
                </v-alert>

                <v-alert
                    v-else-if="!loading && peersTotalCount === 0"
                    type="info"
                    variant="tonal"
                    density="compact"
                    class="mb-0"
                >
                    Nu există alte produse cu acest furnizor pe listă
                    <template v-if="hasActiveFilters">
                        pentru filtrele selectate
                    </template>.
                </v-alert>

                <v-data-table-server
                    v-else
                    v-model="selectedProductCodes"
                    class="propagate-peers-table"
                    :headers="tableHeaders"
                    :items="tableRows"
                    :items-length="peersTotalCount"
                    :page="page"
                    :items-per-page="itemsPerPage"
                    :page-text="peersPageText"
                    :items-per-page-options="itemsPerPageOptions"
                    :loading="loading || previewBuilding"
                    item-value="product_code"
                    show-select
                    density="compact"
                    fixed-header
                    height="360"
                    @update:page="onPageChange"
                    @update:items-per-page="onItemsPerPageChange"
                >
                    <!-- Server tables only select the current page by default;
                         override header checkbox to select every matching peer. -->
                    <template v-slot:[`header.data-table-select`]>
                        <v-checkbox
                            :model-value="allMatchingSelected"
                            :indeterminate="someMatchingSelected"
                            density="compact"
                            hide-details
                            :disabled="!peersTotalCount || saving || loading || selectingAll"
                            aria-label="Selectează toate produsele"
                            @update:model-value="onHeaderSelectToggle"
                        />
                    </template>
                    <template v-slot:[`item.product_code`]="{ item }">
                        <span class="font-weight-medium">{{ item.product_code }}</span>
                        <v-chip
                            v-if="item.hasDraft"
                            class="ms-1"
                            size="x-small"
                            color="warning"
                            variant="tonal"
                        >
                            în lucru
                        </v-chip>
                    </template>
                    <template v-slot:[`item.previous_price`]="{ item }">
                        {{ formatPrice(item.previous_price) }}
                    </template>
                    <template v-slot:[`item.next_price`]="{ item }">
                        <span :class="{ 'text-primary font-weight-medium': item.price_changed }">
                            {{ formatPrice(item.next_price) }}
                        </span>
                    </template>
                    <template #no-data>
                        <div class="text-medium-emphasis pa-4">
                            Niciun produs pe această pagină.
                        </div>
                    </template>
                </v-data-table-server>
            </v-card-text>

            <v-divider class="flex-shrink-0" />
            <v-card-actions class="propagate-dialog-actions flex-shrink-0 pa-4 flex-wrap ga-2">
                <v-spacer class="d-none d-sm-flex" />
                <v-btn
                    variant="text"
                    :disabled="saving"
                    @click="onCancel"
                >
                    Anulează
                </v-btn>
                <v-btn
                    variant="text"
                    :disabled="saving"
                    @click="onSkip"
                >
                    Doar acest produs
                </v-btn>
                <v-btn
                    color="primary"
                    variant="elevated"
                    style="color: white !important"
                    :loading="saving"
                    :disabled="!canApply"
                    @click="onApply"
                >
                    Salvează și aplică
                    <template v-if="selectedProductCodes.length">
                        ({{ selectedProductCodes.length }})
                    </template>
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import HierarchicalFilterSelect from '@/components/pricelist/HierarchicalFilterSelect.vue';
import { getManufacturerCodes } from '@/data/manufacturerCodes';
import { SYSTEM_OPTIONS } from '@/data/productCategories';
import {
    getChapterOptionsForSystems,
    getSubchapterOptionsForSystems,
    listAllChapters,
} from '@/data/productStructureLookups';
import {
    PROPAGATE_FIELD_DEFINITIONS,
    buildPropagatePreviewRows,
    emptyPropagateFilterCriteria,
    extractPropagateValuesFromForm,
    formatPropagateFieldCurrentValue,
    peerLocksMarkupToReference,
} from '@/utils/priceListPropagateFurnizor';
import { isDirectListPropagateField } from '@/utils/priceListProductDetailsKeys';
import { formatDataTablePageText } from '@/utils/dataTablePageText';
import {
    ensureProductLookupsLoaded,
    fetchSubcategoriesForSystem,
} from '@/utils/productLookupApi';
import { diacriticInsensitiveFilter } from '@/utils/textSearch';
import { formatRonPrice } from '@/utils/utils';

const ITEMS_PER_PAGE_OPTIONS = [
    { value: 25, title: '25' },
    { value: 50, title: '50' },
    { value: 100, title: '100' },
];

const PREVIEW_DEBOUNCE_MS = 250;
const FILTER_EMIT_DEBOUNCE_MS = 300;

export default {
    name: 'PriceListPropagateFurnizorFieldsDialog',
    components: {
        HierarchicalFilterSelect,
    },
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        furnizorLabel: {
            type: String,
            default: '',
        },
        sourceProductCode: {
            type: String,
            default: '',
        },
        sourceProductName: {
            type: String,
            default: '',
        },
        peers: {
            type: Array,
            default: () => [],
        },
        /** product_code → full item, including peers not on the current page. */
        peerItemsByCode: {
            type: Object,
            default: () => ({}),
        },
        peersTotalCount: {
            type: Number,
            default: 0,
        },
        page: {
            type: Number,
            default: 1,
        },
        itemsPerPage: {
            type: Number,
            default: 25,
        },
        /** Field keys pre-checked when dialog opens. */
        defaultFieldKeys: {
            type: Array,
            default: () => [],
        },
        /** Source product builder form — used for checkbox tooltips + next values. */
        sourceForm: {
            type: Object,
            default: null,
        },
        /** Active furnizor identity for peer pricing preview. */
        identity: {
            type: Object,
            default: null,
        },
        pricingSettings: {
            type: Object,
            default: () => ({}),
        },
        saving: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        /**
         * Resolves every matching peer code for the current furnizor/filters.
         * ({ search?: string, criteria?: Object }) => Promise<string[]>
         */
        resolveAllPeerCodes: {
            type: Function,
            default: null,
        },
        /**
         * Salvează în listă: Detalii produs, note furnizor, termen de plată, adaos referință.
         * Price fields stay available when saving to lista în lucru.
         */
        directListSave: {
            type: Boolean,
            default: false,
        },
    },
    emits: [
        'update:modelValue',
        'skip',
        'apply',
        'cancel',
        'update:page',
        'update:itemsPerPage',
        'search',
        'filters',
    ],
    data() {
        return {
            fieldDefinitions: PROPAGATE_FIELD_DEFINITIONS,
            itemsPerPageOptions: [...ITEMS_PER_PAGE_OPTIONS],
            tableHeaders: [
                { title: 'Cod', key: 'product_code', sortable: false },
                { title: 'Denumire', key: 'product_name', sortable: false },
                { title: 'Valoare curentă', key: 'previous_value', sortable: false },
                { title: 'Valoare nouă', key: 'next_value', sortable: false },
                {
                    title: 'Preț vânzare vechi cu TVA',
                    key: 'previous_price',
                    sortable: false,
                    align: 'end',
                },
                {
                    title: 'Preț vânzare nou cu TVA',
                    key: 'next_price',
                    sortable: false,
                    align: 'end',
                },
            ],
            selectedFieldKeys: [],
            selectedProductCodes: [],
            criteria: emptyPropagateFilterCriteria(),
            subcategoryOptions: [],
            loadingSubcategories: false,
            lookupsVersion: 0,
            diacriticInsensitiveFilter,
            filterEmitTimer: null,
            previewBuildTimer: null,
            previewRows: [],
            previewBuilding: false,
            selectingAll: false,
        };
    },
    computed: {
        categoryOptions() {
            return SYSTEM_OPTIONS;
        },
        manufacturerOptions() {
            void this.lookupsVersion;
            return getManufacturerCodes()
                .filter((entry) => String(entry?.code || '').trim())
                .map((entry) => {
                    const code = String(entry.code).trim();
                    const name = String(entry.name || '').trim();
                    return {
                        title: name ? `${code} – ${name}` : code,
                        value: code,
                    };
                })
                .sort((a, b) => a.title.localeCompare(b.title, 'ro'));
        },
        hasSystemFilter() {
            return Array.isArray(this.criteria.systems) && this.criteria.systems.length > 0;
        },
        hasChapterFilter() {
            if (Array.isArray(this.criteria.chapterCode)) {
                return this.criteria.chapterCode.length > 0;
            }
            return Boolean(this.criteria.chapterCode);
        },
        chapterOptions() {
            void this.lookupsVersion;
            const chapters = this.hasSystemFilter
                ? getChapterOptionsForSystems(this.criteria.systems)
                : listAllChapters().map((chapter) => ({
                    code: chapter.code,
                    name: chapter.name,
                    title: chapter.name
                        ? `${chapter.code} – ${chapter.name}`
                        : chapter.code,
                }));
            return chapters.map((option) => ({
                title: option.title
                    || (option.name ? `${option.code} – ${option.name}` : option.code),
                value: option.code,
            }));
        },
        subchapterOptions() {
            void this.lookupsVersion;
            const chapterCodes = Array.isArray(this.criteria.chapterCode)
                ? this.criteria.chapterCode
                : (this.criteria.chapterCode ? [this.criteria.chapterCode] : []);
            if (!chapterCodes.length) {
                return [];
            }
            const systems = this.hasSystemFilter
                ? this.criteria.systems
                : SYSTEM_OPTIONS.map((option) => option.value);
            const byCode = new Map();
            for (const chapterCode of chapterCodes) {
                for (const option of getSubchapterOptionsForSystems(systems, chapterCode)) {
                    if (!byCode.has(option.code)) {
                        byCode.set(option.code, {
                            title: option.title
                                || (option.name
                                    ? `${option.code} – ${option.name}`
                                    : option.code),
                            value: option.code,
                        });
                    }
                }
            }
            return Array.from(byCode.values()).sort((a, b) => (
                Number(a.value) - Number(b.value)
                || String(a.value).localeCompare(String(b.value), 'ro')
            ));
        },
        hasActiveFilters() {
            const c = this.criteria;
            return Boolean(
                String(c.search || '').trim()
                || (Array.isArray(c.systems) && c.systems.length)
                || String(c.systemSubcategory || '').trim()
                || String(c.manufacturerCode || '').trim()
                || (Array.isArray(c.chapterCode) && c.chapterCode.length)
                || String(c.subchapterCode || '').trim(),
            );
        },
        pageProductCodes() {
            return (this.peers || [])
                .map((peer) => peer.product_code)
                .filter(Boolean);
        },
        /** Selectable peer count (source product already excluded by parent). */
        selectablePeersEstimate() {
            const total = Math.max(0, Number(this.peersTotalCount) || 0);
            if (total <= 0) {
                return 0;
            }
            return Math.max(total, this.pageProductCodes.length);
        },
        allMatchingSelected() {
            const selected = this.selectedProductCodes.length;
            const estimate = this.selectablePeersEstimate;
            return estimate > 0 && selected >= estimate;
        },
        someMatchingSelected() {
            return this.selectedProductCodes.length > 0 && !this.allMatchingSelected;
        },
        markupReferenceLockedCodes() {
            if (!this.selectedFieldKeys.includes('markupReference')) {
                return [];
            }
            const byCode = { ...(this.peerItemsByCode || {}) };
            for (const peer of this.peers || []) {
                const code = String(peer?.product_code || '').trim();
                const item = peer?.item || peer;
                if (code && item) {
                    byCode[code] = item;
                }
            }
            return this.selectedProductCodes.filter((code) => {
                const key = String(code || '').trim();
                const item = byCode[key];
                return Boolean(item && peerLocksMarkupToReference(item, this.identity));
            });
        },
        markupReferenceLockedMessage() {
            const count = this.markupReferenceLockedCodes.length;
            if (!count) {
                return '';
            }
            const noun = count === 1 ? 'produs are' : 'produse au';
            if (this.directListSave) {
                return `${count} ${noun} „Adaos = referință”. La acestea se actualizează și adaosul actual și prețul de listă. Salvează în listă le omite; folosește Salvează în lucru.`;
            }
            return `${count} ${noun} „Adaos = referință”. La acestea se actualizează și adaosul actual și prețul de listă.`;
        },
        canApply() {
            return (
                this.selectedFieldKeys.length > 0
                && this.selectedProductCodes.length > 0
                && !this.saving
                && !this.loading
                && !this.selectingAll
            );
        },
        previewBuildKey() {
            if (!this.modelValue) {
                return '';
            }
            return JSON.stringify({
                codes: (this.peers || []).map((peer) => peer.product_code),
                fields: this.selectedFieldKeys,
                sourceCode: this.sourceProductCode,
            });
        },
        peersPageText() {
            const visibleCount = this.tableRows.length;
            return formatDataTablePageText({
                total: visibleCount > 0 ? this.peersTotalCount : 0,
                visibleCount,
                page: this.page,
                itemsPerPage: this.itemsPerPage,
            });
        },
        tableRows() {
            if (this.previewRows.length) {
                return this.previewRows;
            }
            // Lightweight fallback while preview builds / before fields selected.
            return (this.peers || []).map((peer) => ({
                product_code: peer.product_code,
                product_name: peer.product_name,
                previous_value: this.selectedFieldKeys.length ? '…' : '—',
                next_value: this.selectedFieldKeys.length ? '…' : '—',
                previous_price: peer.price,
                next_price: peer.price,
                price_changed: false,
                hasDraft: peer.hasDraft,
            }));
        },
    },
    watch: {
        modelValue(isOpen) {
            if (isOpen) {
                this.resetSelection();
                void this.bootstrapLookups();
            } else {
                this.clearFilterEmitDebounce();
                this.clearPreviewDebounce();
            }
        },
        previewBuildKey(key) {
            if (!key) {
                this.previewRows = [];
                return;
            }
            this.schedulePreviewBuild();
        },
        selectedFieldKeys() {
            this.schedulePreviewBuild();
        },
        peers() {
            this.schedulePreviewBuild();
        },
    },
    beforeUnmount() {
        this.clearFilterEmitDebounce();
        this.clearPreviewDebounce();
    },
    methods: {
        fieldCurrentValue(fieldKey) {
            return formatPropagateFieldCurrentValue(fieldKey, this.sourceForm);
        },
        isFieldDisabledForDirectSave(fieldKey) {
            return this.directListSave && !isDirectListPropagateField(fieldKey);
        },
        selectableFieldKeys() {
            return PROPAGATE_FIELD_DEFINITIONS
                .map((field) => field.key)
                .filter((key) => !this.isFieldDisabledForDirectSave(key));
        },
        resetSelection() {
            const allowed = new Set(this.selectableFieldKeys());
            const defaults = Array.isArray(this.defaultFieldKeys)
                ? this.defaultFieldKeys.filter((key) => allowed.has(key))
                : [];
            this.selectedFieldKeys = [...defaults];
            this.criteria = emptyPropagateFilterCriteria();
            this.subcategoryOptions = [];
            this.clearFilterEmitDebounce();
            this.selectingAll = false;
            // Same as bulk update: no auto-selection; user picks rows or uses buttons.
            this.selectedProductCodes = [];
            this.previewRows = [];
        },
        selectAllFields() {
            this.selectedFieldKeys = this.selectableFieldKeys();
        },
        clearFields() {
            this.selectedFieldKeys = [];
        },
        criteriaPayload() {
            const c = { ...this.criteria };
            if (!this.hasSystemFilter) {
                c.systemSubcategory = null;
            }
            if (!this.hasChapterFilter) {
                c.subchapterCode = null;
            }
            return {
                search: String(c.search || '').trim(),
                systems: Array.isArray(c.systems) ? [...c.systems] : [],
                systemSubcategory: c.systemSubcategory || null,
                manufacturerCode: c.manufacturerCode || null,
                chapterCode: Array.isArray(c.chapterCode) ? [...c.chapterCode] : [],
                subchapterCode: c.subchapterCode || null,
            };
        },
        onCriteriaInput() {
            this.clearFilterEmitDebounce();
            this.filterEmitTimer = setTimeout(() => {
                this.filterEmitTimer = null;
                this.emitFilters();
            }, FILTER_EMIT_DEBOUNCE_MS);
        },
        emitFilters() {
            const payload = this.criteriaPayload();
            this.selectedProductCodes = [];
            this.$emit('filters', payload);
            // Backward-compatible search event for older parents.
            this.$emit('search', payload.search);
        },
        clearFilterEmitDebounce() {
            if (this.filterEmitTimer) {
                clearTimeout(this.filterEmitTimer);
                this.filterEmitTimer = null;
            }
        },
        async onSystemsFilterChange() {
            this.criteria.systemSubcategory = null;
            this.subcategoryOptions = [];
            if (!this.hasSystemFilter) {
                this.onCriteriaInput();
                return;
            }
            this.loadingSubcategories = true;
            try {
                const systems = this.criteria.systems || [];
                const optionLists = await Promise.all(
                    systems.map((system) => fetchSubcategoriesForSystem(system)),
                );
                const byValue = new Map();
                for (const list of optionLists) {
                    for (const option of list || []) {
                        const value = option?.value ?? option?.code ?? option;
                        const title = option?.title
                            || option?.name
                            || String(value || '');
                        if (value != null && String(value).trim() && !byValue.has(value)) {
                            byValue.set(value, { title, value });
                        }
                    }
                }
                this.subcategoryOptions = Array.from(byValue.values());
            } catch (_error) {
                this.subcategoryOptions = [];
            } finally {
                this.loadingSubcategories = false;
                this.onCriteriaInput();
            }
        },
        onChapterFilterChange() {
            this.criteria.subchapterCode = null;
            this.onCriteriaInput();
        },
        async bootstrapLookups() {
            try {
                await ensureProductLookupsLoaded();
            } catch (_error) {
                // Options fall back to whatever is already cached.
            }
            this.lookupsVersion += 1;
        },
        schedulePreviewBuild() {
            this.clearPreviewDebounce();
            if (!this.modelValue) {
                return;
            }
            this.previewBuilding = true;
            this.previewBuildTimer = setTimeout(() => {
                this.previewBuildTimer = null;
                this.rebuildPreviewRows();
            }, PREVIEW_DEBOUNCE_MS);
        },
        clearPreviewDebounce() {
            if (this.previewBuildTimer) {
                clearTimeout(this.previewBuildTimer);
                this.previewBuildTimer = null;
            }
        },
        rebuildPreviewRows() {
            try {
                const keys = this.selectedFieldKeys;
                const values = keys.length
                    ? extractPropagateValuesFromForm(this.sourceForm || {}, keys)
                    : {};
                this.previewRows = buildPropagatePreviewRows({
                    peers: this.peers || [],
                    fieldKeys: keys,
                    sourceForm: this.sourceForm,
                    identity: this.identity,
                    values,
                    pricingSettings: this.pricingSettings,
                });
            } catch (_error) {
                this.previewRows = (this.peers || []).map((peer) => ({
                    product_code: peer.product_code,
                    product_name: peer.product_name,
                    previous_value: '—',
                    next_value: '—',
                    previous_price: peer.price,
                    next_price: peer.price,
                    price_changed: false,
                    hasDraft: peer.hasDraft,
                }));
            } finally {
                this.previewBuilding = false;
            }
        },
        async selectAllProducts() {
            if (this.selectingAll || this.saving || this.loading) {
                return;
            }
            if (typeof this.resolveAllPeerCodes !== 'function') {
                this.selectedProductCodes = [...this.pageProductCodes];
                return;
            }
            this.selectingAll = true;
            try {
                const payload = this.criteriaPayload();
                const codes = await this.resolveAllPeerCodes({
                    search: payload.search,
                    criteria: payload,
                });
                this.selectedProductCodes = Array.isArray(codes)
                    ? codes.filter(Boolean)
                    : [];
            } catch (_error) {
                this.selectedProductCodes = [...this.pageProductCodes];
            } finally {
                this.selectingAll = false;
            }
        },
        onHeaderSelectToggle(checked) {
            if (checked) {
                void this.selectAllProducts();
                return;
            }
            this.clearProducts();
        },
        clearProducts() {
            this.selectedProductCodes = [];
        },
        onPageChange(page) {
            this.$emit('update:page', Number(page) || 1);
        },
        onItemsPerPageChange(size) {
            this.$emit('update:itemsPerPage', Number(size) || 25);
        },
        formatPrice(value) {
            if (value === null || value === undefined || value === '') {
                return '—';
            }
            return formatRonPrice(value);
        },
        onCancel() {
            this.$emit('cancel');
            this.$emit('update:modelValue', false);
        },
        onSkip() {
            this.$emit('skip');
            this.$emit('update:modelValue', false);
        },
        onApply() {
            if (!this.canApply) {
                return;
            }
            const allowed = new Set(this.selectableFieldKeys());
            const fieldKeys = this.selectedFieldKeys.filter((key) => allowed.has(key));
            let targetProductCodes = [...this.selectedProductCodes];
            if (this.directListSave && fieldKeys.includes('markupReference')) {
                const locked = new Set(
                    this.markupReferenceLockedCodes.map((code) => String(code || '').trim()),
                );
                targetProductCodes = targetProductCodes.filter(
                    (code) => !locked.has(String(code || '').trim()),
                );
            }
            this.$emit('apply', {
                fieldKeys,
                targetProductCodes,
            });
        },
    },
};
</script>

<style scoped>
.propagate-dialog-card {
    max-height: min(90vh, 960px);
}

.propagate-dialog-body {
    min-height: 0;
}

.propagate-dialog-actions {
    background: rgb(var(--v-theme-surface));
}

.propagate-peers-table {
    border-radius: 4px;
}
</style>
